/* ===========================================================
   The Olive Architects — Product Carousel (robust loader)
   Shortcodes supported:
     [collection-carousel="collection-handle"]
     [product-feature="product-handle"]
   - Collection: card slider (whole card clickable + Add to Cart)
   - Product feature: full-width, image-left; details right;
     CTA (View Product + Add To Cart) overlay on image (bottom-left)
   - Handles late-injected article content (Elementor/Sections) via
     MutationObserver and waits for Swiper if needed.
   =========================================================== */

(function () {
  /* Where to look for the article body (covers native + Elementor templates) */
  const ARTICLE_SELECTOR =
    '.article-template, .article__content-inner, .article-template__content, .article-template__content.rte, .blog__article__content.rte, .article .rte, .article-content, .rte, .elementor-widget-theme-post-content, rte-formatter';

  const HAS_SHORTCODE_RE = /\[(?:collection-carousel|product-feature)="[^"]+"\]/;

  ready(initWithObserver);

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
    /* Run again after full load in case content is injected very late */
    window.addEventListener('load', fn, { once: true });
  }

  function initWithObserver() {
    processAll();

    /* If any shortcode text remains, observe for DOM injections and re-run */
    if (document.body && document.body.textContent && HAS_SHORTCODE_RE.test(document.body.textContent)) {
      const mo = new MutationObserver(() => {
        const changed = processAll();
        if (!HAS_SHORTCODE_RE.test(document.body.textContent)) {
          mo.disconnect();
        }
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  /* Replace shortcodes in every matching container and build UI */
  function processAll() {
    let didSomething = false;
    const containers = document.querySelectorAll(ARTICLE_SELECTOR);
    containers.forEach(container => {
      const html = container.innerHTML;
      if (!HAS_SHORTCODE_RE.test(html)) return;

      /* Inject collection wrappers */
      let cIdx = 0;
      let replaced = html.replace(/\[collection-carousel="([^"]+)"\]/g, (_, handle) => {
        didSomething = true;
        const id = `toa-carousel-${handle}-${cIdx++}`;
        return `
          <section id="${id}" class="toa-slider-wrapper toa-slider" data-collection="${handle}">
            <div class="swiper-container"><div class="swiper-wrapper"></div></div>
          </section>`;
      });

      /* Inject single product feature wrappers */
      let pIdx = 0;
      replaced = replaced.replace(/\[product-feature="([^"]+)"\]/g, (_, handle) => {
        didSomething = true;
        const id = `toa-feature-${handle}-${pIdx++}`;
        return `
          <section id="${id}" class="toa-feature-wrapper" data-product="${handle}">
            <div class="toa-feature">
              <div class="toa-feature__media">
                <div class="toa-feature__img toa-skel"></div>
                <!-- CTA overlay on image (bottom-left) -->
                <div class="toa-feature__cta-overlay">
                  <a class="toa-btn toa-btn--ghost toa-feature__view" href="#" target="_self" rel="noopener">View Product</a>
                  <form class="toa-feature__purchase" action="/cart/add" method="post">
                    <input type="hidden" name="id" value="">
                    <button type="submit" class="toa-btn toa-btn--primary">Add To Cart</button>
                  </form>
                </div>
              </div>
              <div class="toa-feature__details">
                <h2 class="toa-feature__title toa-skel">Loading…</h2>
                <div class="toa-feature__desc toa-skel"></div>
                <div class="toa-feature__price-row">
                  <span class="toa-feature__price toa-skel">$—</span>
                </div>
                <div class="toa-feature__variant-label">Variant</div>
                <div class="toa-feature__variant-grid" role="listbox" aria-label="Select variant"></div>
              </div>
            </div>
          </section>`;
      });

      if (replaced !== html) {
        container.innerHTML = replaced;
      }
    });

    buildAll();
    return didSomething;
  }

  function buildAll() {
    document.querySelectorAll('.toa-slider-wrapper[data-collection]').forEach(buildCollectionCarousel);
    document.querySelectorAll('.toa-feature-wrapper[data-product]').forEach(buildProductFeature);
  }

  /* ===================== COLLECTIONS ===================== */
  function buildCollectionCarousel(wrapper) {
    if (wrapper.__built) return;
    wrapper.__built = true;

    const handle   = wrapper.getAttribute('data-collection');
    const trackEl  = wrapper.querySelector('.swiper-wrapper');
    const swiperEl = wrapper.querySelector('.swiper-container');

    fetch(`/collections/${handle}/products.json?limit=12`)
      .then(r => r.json())
      .then(json => {
        (json.products || []).forEach(product => {
          const variant = product.variants && product.variants[0];
          if (!variant) return;

          const imgSrc   = getPreferredCollectionImage(product.images); // objects with {src}
          const priceTxt = formatPrice(variant.price);

          const slide = document.createElement('div');
          slide.className = 'swiper-slide';
          if (imgSrc) slide.style.backgroundImage = `url(${imgSrc})`;

          /* Make entire slide clickable; keep add-to-cart button */
          slide.innerHTML = `
            <a class="toa-slide-hit" href="/products/${product.handle}" aria-label="View ${escapeHtml(product.title)}"></a>
            <div class="product-name">${escapeHtml(product.title)}</div>
            <div class="product-price">${priceTxt}</div>
            <form class="purchase-form" action="/cart/add" method="post">
              <input type="hidden" name="id" value="${variant.id}">
              <button type="submit" class="purchase-btn" aria-label="Add ${escapeHtml(product.title)} to cart">
                Add To Cart
              </button>
            </form>
          `;
          trackEl.appendChild(slide);
        });

        waitForSwiper(() => {
          new Swiper(swiperEl, {
            freeMode: true,
            slidesPerView: 'auto',
            spaceBetween: 12,
            loop: false,
            navigation: false,
            pagination: false
          });
        });
      })
      .catch(err => console.error('TOA collection carousel error:', err));
  }

  /* ===================== SINGLE PRODUCT FEATURE ===================== */
  function buildProductFeature(wrapper) {
    if (wrapper.__built) return;
    wrapper.__built = true;

    const handle  = wrapper.getAttribute('data-product');
    const card    = wrapper.querySelector('.toa-feature');

    const imgBox  = card.querySelector('.toa-feature__img');
    const titleEl = card.querySelector('.toa-feature__title');
    const descEl  = card.querySelector('.toa-feature__desc');
    const priceEl = card.querySelector('.toa-feature__price');
    const gridEl  = card.querySelector('.toa-feature__variant-grid');
    const labelEl = card.querySelector('.toa-feature__variant-label');
    const form    = card.querySelector('.toa-feature__purchase');
    const idInput = form.querySelector('input[name="id"]');
    const viewA   = card.querySelector('.toa-feature__view');

    viewA.href = `/products/${handle}`;

    fetch(`/products/${handle}.js`)
      .then(r => r.json())
      .then(prod => {
        if (!prod || !Array.isArray(prod.variants) || prod.variants.length === 0) {
          titleEl.textContent = 'Product unavailable';
          card.classList.add('toa-feature--error');
          return;
        }

        /* IMAGE: /products/{handle}.js returns an array of STRING URLs */
        const heroImg =
          (Array.isArray(prod.images) && (prod.images[1] || prod.images[0])) ||
          prod.featured_image || '';
        if (heroImg) {
          imgBox.classList.remove('toa-skel');
          imgBox.style.backgroundImage = `url("${heroImg}")`;
        }

        /* TITLE + DESCRIPTION (trimmed) */
        titleEl.classList.remove('toa-skel');
        titleEl.textContent = prod.title;

        const clean = stripHtml(prod.description || '');
        const short = clean.length > 280 ? clean.slice(0, 277) + '…' : clean;
        descEl.classList.remove('toa-skel');
        descEl.textContent = short;

        /* VARIANTS: hide pills if single “Default Title”/“Default” */
        const variants = prod.variants;
        const firstVar = variants[0];
        idInput.value  = firstVar.id; // always set default variant id
        priceEl.classList.remove('toa-skel');
        priceEl.textContent = formatPrice(firstVar.price);

        const singleDefault =
          variants.length === 1 &&
          (firstVar.title === 'Default Title' || firstVar.title === 'Default');

        if (singleDefault) {
          if (labelEl) labelEl.style.display = 'none';
          if (gridEl)  gridEl.style.display  = 'none';
          return; // no selector needed
        }

        // Build variant buttons (pills)
        const btns = variants.map(v => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'toa-var-btn';
          b.textContent = v.title;
          b.dataset.id = v.id;
          b.dataset.price = v.price;
          b.setAttribute('role', 'option');
          b.setAttribute('aria-label', `Select ${v.title}`);
          return b;
        });

        btns.forEach(b => gridEl.appendChild(b));

        function selectVariant(btn) {
          btns.forEach(x => x.classList.remove('is-active'));
          btn.classList.add('is-active');
          idInput.value = btn.dataset.id;
          priceEl.textContent = formatPrice(btn.dataset.price);
        }

        selectVariant(btns[0]);                              // default
        btns.forEach(b => b.addEventListener('click', () => selectVariant(b)));
      })
      .catch(err => {
        titleEl.textContent = 'Error loading product';
        console.error('TOA product feature error:', err);
      });
  }

  /* ===================== Helpers ===================== */

  function waitForSwiper(cb) {
    if (window.Swiper) return cb();
    const iv = setInterval(() => {
      if (window.Swiper) { clearInterval(iv); cb(); }
    }, 50);
    setTimeout(() => clearInterval(iv), 3000);
  }

  /* collections endpoint returns array of objects: [{src: "..."}] */
  function getPreferredCollectionImage(images) {
    if (!images || !images.length) return '';
    const first  = images[0] && images[0].src;
    const second = images[1] && images[1].src;
    return first || second || '';
  }

  /* /products/{handle}.js price may be cents (number) or string "12.00" */
  function formatPrice(raw) {
    let dollars;
    if (typeof raw === 'number') {
      dollars = raw / 100;
    } else {
      const s = String(raw).trim();
      if (/^\d+$/.test(s)) dollars = parseInt(s, 10) / 100;    // cents
      else if (/^\d+(\.\d+)?$/.test(s)) dollars = parseFloat(s);
      else dollars = 0;
    }
    return dollars.toLocaleString(undefined, {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  function stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
})();
